import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Plus, Edit, Eye, Users, Clock, CheckCircle, AlertCircle, Play, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {api} from '../../api';

const AdminQuizList: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'my-quizzes' | 'pending-reviews'>('my-quizzes');
  const [quizzes, setQuizzes] = useState<any[]>([]);
  const [pendingReviews, setPendingReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [quizToDelete, setQuizToDelete] = useState<string | null>(null);

  useEffect(() => {
    if (activeTab === 'my-quizzes') {
      loadQuizzes();
    } else {
      loadPendingReviews();
    }
  }, [activeTab]);

  const loadQuizzes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/quizzes');

      setQuizzes(response.data.data);
      console.log('Fetched Quizzes (raw response):', response.data);
      console.log('Quizzes state after setting:', response.data.data);
    } catch (error) {
      console.error('Load quizzes error:', error);
      toast.error('Erreur lors du chargement des quiz');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingReviews = async () => {
    try {
      setLoading(true);
      const response = await api.get('/quizzes/pending-reviews');

      setPendingReviews(response.data.pendingReviews || []);
      console.log('Fetched Pending Reviews:', response.data.pendingReviews);
    } catch (error) {
      console.error('Load pending reviews error:', error);
      toast.error('Erreur lors du chargement des révisions en attente');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuiz = () => {
    navigate('/admin/quizzes/create');
  };

  const handleEditQuiz = (quizId: string) => {
    navigate(`/admin/quizzes/${quizId}/edit`);
  };

  const handleViewQuiz = (quizId: string) => {
    navigate(`/admin/quizzes/${quizId}`);
  };

  const handlePreviewQuiz = (quizId: string) => {
    navigate(`/admin/quizzes/${quizId}/admin-preview`);
  };

  const confirmDeleteQuiz = (quizId: string) => {
    setQuizToDelete(quizId);
    setShowDeleteModal(true);
  };

  const handleDeleteQuiz = async () => {
    if (!quizToDelete) return;
    
    try {
      await api.delete(`/quizzes/${quizToDelete}`);
    } catch (error) {
      console.error('Delete quiz error:', error);
      toast.error('Erreur lors de la suppression du quiz');
    }
  };

  const handleReviewQuiz = (attemptId: string) => {
    navigate(`/admin/quizzes/review/${attemptId}`);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-[#FFB639]/15 text-[#FFB639]';
      case 'intermediate': return 'bg-[#FFB639]/15 text-[#FFB639]';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyLabel = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'Débutant';
      case 'intermediate': return 'Intermédiaire';
      case 'advanced': return 'Avancé';
      default: return difficulty;
    }
  };

  const getScoreColor = (score: number, passingScore: number) => {
    if (score >= passingScore) return 'text-[#FFB639]';
    if (score >= passingScore * 0.8) return 'text-[#FFB639]';
    return 'text-red-600';
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Quiz & Évaluations</h1>
          <button
            onClick={handleCreateQuiz}
            className="flex items-center px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Créer un quiz
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('my-quizzes')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'my-quizzes'
                  ? 'border-[#FFB639] text-[#FFB639]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Mes Quiz ({quizzes.length})
            </button>
            <button
              onClick={() => setActiveTab('pending-reviews')}
              className={`py-2 px-1 border-b-2 font-medium text-sm relative ${
                activeTab === 'pending-reviews'
                  ? 'border-[#FFB639] text-[#FFB639]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              À corriger ({pendingReviews.length})
              {pendingReviews.length > 0 && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></div>
              )}
            </button>
          </nav>
        </div>

        {/* My Quizzes Tab */}
        {activeTab === 'my-quizzes' && (
          <div className="space-y-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFB639]"></div>
              </div>
            ) : quizzes.length > 0 ? (
              quizzes.map((quiz) => (
                <div key={quiz._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{quiz.title}</h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDifficultyColor(quiz.difficulty)}`}>
                          {getDifficultyLabel(quiz.difficulty)}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          quiz.isActive ? 'bg-[#FFB639]/15 text-[#FFB639]' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {quiz.isActive ? 'Actif' : 'Inactif'}
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-4">{quiz.course?.title || 'Cours non spécifié'}</p>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center text-gray-500">
                          <Users className="h-4 w-4 mr-1" />
                          {quiz.analytics?.totalAttempts || 0} tentatives
                        </div>
                        <div className="flex items-center text-gray-500">
                          <Clock className="h-4 w-4 mr-1" />
                          {quiz.settings?.timeLimit || 30} minutes
                        </div>
                        <div className="flex items-center text-gray-500">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          {quiz.questions?.length || 0} questions
                        </div>
                        <div className={`flex items-center ${getScoreColor(quiz.analytics?.averageScore || 0, quiz.settings?.passingScore || 70)}`}>
                          <span className="font-medium">
                            Moy: {quiz.analytics?.averageScore?.toFixed(0) || 0}%
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleViewQuiz(quiz._id)}
                        className="p-2 text-gray-400 hover:text-[#FFB639] transition-colors"
                        title="Voir les résultats"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditQuiz(quiz._id)}
                        className="p-2 text-gray-400 hover:text-[#FFB639] transition-colors"
                        title="Modifier"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handlePreviewQuiz(quiz._id)}
                        className="p-2 text-gray-400 hover:text-[#FFB639] transition-colors"
                        title="Prévisualiser"
                      >
                        <Play className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => confirmDeleteQuiz(quiz._id)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <CheckCircle className="h-16 w-16 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Aucun quiz créé
                </h3>
                <p className="text-gray-500 mb-4">
                  Commencez par créer votre premier quiz
                </p>
                <button
                  onClick={handleCreateQuiz}
                  className="px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
                >
                  Créer un quiz
                </button>
              </div>
            )}
          </div>
        )}

        {/* Pending Reviews Tab */}
        {activeTab === 'pending-reviews' && (
          <div className="space-y-4">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFB639]"></div>
              </div>
            ) : pendingReviews.length > 0 ? (
              pendingReviews.map((review) => (
                <div key={review._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{review.student?.name || 'Étudiant inconnu'}</h3>
                        <span className="px-2 py-1 text-xs font-medium bg-[#FFB639]/15 text-[#FFB639] rounded-full flex items-center">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          En attente
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-1">{review.quiz?.title || 'Quiz inconnu'}</p>
                      <p className="text-xs text-gray-500 mb-4">{review.course?.title || 'Cours inconnu'}</p>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center text-gray-500">
                          <span>Soumis le:</span>
                          <span className="ml-1 font-medium">
                            {new Date(review.submittedAt).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                        <div className="flex items-center text-gray-500">
                          <span>Score auto:</span>
                          <span className="ml-1 font-medium">
                            {review.autoScore || 0}/{review.answers?.length || 0}
                          </span>
                        </div>
                        <div className="flex items-center text-gray-500">
                          <span>Questions ouvertes:</span>
                          <span className="ml-1 font-medium text-[#FFB639]">
                            {review.shortAnswers || 0}
                          </span>
                        </div>
                        <div className="flex items-center text-gray-500">
                          <span>Email:</span>
                          <span className="ml-1 text-[#FFB639] text-xs">
                            {review.student?.email || 'Email inconnu'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="ml-4">
                      <button
                        onClick={() => handleReviewQuiz(review._id)}
                        className="px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
                      >
                        Corriger
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <CheckCircle className="h-16 w-16 text-[#FFB639] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Aucune correction en attente
                </h3>
                <p className="text-gray-500">
                  Toutes les soumissions ont été corrigées.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Confirmer la suppression</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-700">
                  Êtes-vous sûr de vouloir supprimer ce quiz ? Cette action est irréversible et supprimera également toutes les tentatives associées.
                </p>
              </div>
              <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeleteQuiz}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminQuizList;